/**
 * MapMarkers - Sistema de marcadores accesibles por tipo de infraestructura
 *
 * Implementa medidas E02 (contraste) y E04 (daltonismo)
 *
 * @module services/tiles/MapMarkers
 * @version 1.0.0
 * @since V09
 *
 * ## Características
 * - Colores distintivos por tipo de infraestructura
 * - Paleta daltonismo-safe
 * - Iconos SVG inline para rendimiento
 * - Compatible con Leaflet y OpenLayers
 */

import { InfrastructureType } from '../../types/infrastructure';

// ============================================================================
// TIPOS
// ============================================================================

/** Configuración visual de un marcador */
export interface MarkerConfig {
  /** ID del tipo de infraestructura */
  type: InfrastructureType;
  /** Color principal (hex) */
  color: string;
  /** Color de contraste para texto/icono (hex) */
  contrastColor: string;
  /** Nombre legible */
  label: string;
  /** Icono SVG path (solo el path d="...") */
  iconPath: string;
  /** Emoji fallback */
  emoji: string;
  /** Categoría para agrupación */
  category: 'emergency' | 'services' | 'utilities' | 'infrastructure';
}

/** Tamaño de marcador */
export type MarkerSize = 'small' | 'medium' | 'large';

/** Opciones para generar SVG */
export interface MarkerSvgOptions {
  size?: MarkerSize;
  selected?: boolean;
  disabled?: boolean;
  withShadow?: boolean;
}

// ============================================================================
// CONSTANTES - TAMAÑOS
// ============================================================================

export const MARKER_SIZES: Record<MarkerSize, { width: number; height: number; iconScale: number }> = {
  small: { width: 24, height: 32, iconScale: 0.5 },
  medium: { width: 32, height: 42, iconScale: 0.65 },
  large: { width: 40, height: 52, iconScale: 0.8 },
};

// ============================================================================
// CONSTANTES - ICONOS SVG (paths simplificados)
// ============================================================================

/** Paths SVG para iconos (basados en heroicons/lucide, 24x24 viewBox) */
const ICON_PATHS = {
  // Emergencias
  health: 'M12 4v16m-8-8h16', // Cruz médica
  fire: 'M12 2c-2 4-4 6-4 9a4 4 0 008 0c0-3-2-5-4-9z', // Llama
  police: 'M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z', // Estrella
  emergency: 'M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', // Alerta
  
  // Servicios
  education: 'M12 3L1 9l11 6 9-5v7M5 13v4c0 2 3 3 7 3s7-1 7-3v-4', // Graduación
  cultural: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6', // Museo
  religious: 'M12 2v4m0 14v2M4 8h16M6 8v13h12V8', // Iglesia
  sports: 'M12 12m-9 0a9 9 0 1018 0 9 9 0 10-18 0M12 3v18M3 12h18', // Balón
  social: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z', // Personas
  
  // Utilities
  fuel: 'M3 22V6a2 2 0 012-2h6a2 2 0 012 2v4h2a2 2 0 012 2v10M7 2v4', // Gasolina
  hydraulic: 'M12 2v6m0 4v10M5 12c0-4 3-6 7-6s7 2 7 6-3 6-7 6-7-2-7-6z', // Gota
  energy: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z', // Rayo
  telecom: 'M12 20V10M18 20V4M6 20v-4', // Antena
  
  // Infraestructura
  municipal: 'M3 21h18M9 21V9h6v12M5 9V7l7-4 7 4v2', // Ayuntamiento
  vial: 'M4 12h16M12 4v16M8 8l8 8M16 8l-8 8', // Cruce
  industrial: 'M3 21h18M4 21V10l4-6h8l4 6v11M9 21v-4h6v4', // Fábrica
  generic: 'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2', // Círculo
};

// ============================================================================
// PALETA DE COLORES DALTONISMO-SAFE
// Basada en recomendaciones de ColorBrewer y Wong (2011)
// ============================================================================

/**
 * Configuración de marcadores por tipo de infraestructura
 *
 * Colores elegidos para:
 * 1. Distinguibilidad con daltonismo (protanopia, deuteranopia, tritanopia)
 * 2. Contraste WCAG ≥3:1 sobre fondo blanco
 * 3. Agrupación semántica por categoría
 */
export const MARKER_CONFIGS: Record<InfrastructureType, MarkerConfig> = {
  // === EMERGENCIAS (rojos/naranjas) ===
  [InfrastructureType.HEALTH]: {
    type: InfrastructureType.HEALTH,
    color: '#dc2626', // Rojo médico
    contrastColor: '#ffffff',
    label: 'Sanitario',
    iconPath: ICON_PATHS.health,
    emoji: '🏥',
    category: 'emergency',
  },
  [InfrastructureType.FIRE]: {
    type: InfrastructureType.FIRE,
    color: '#ea580c', // Naranja fuego
    contrastColor: '#ffffff',
    label: 'Bomberos',
    iconPath: ICON_PATHS.fire,
    emoji: '🚒',
    category: 'emergency',
  },
  [InfrastructureType.POLICE]: {
    type: InfrastructureType.POLICE,
    color: '#1d4ed8', // Azul policía
    contrastColor: '#ffffff',
    label: 'Policial',
    iconPath: ICON_PATHS.police,
    emoji: '👮',
    category: 'emergency',
  },
  [InfrastructureType.EMERGENCY]: {
    type: InfrastructureType.EMERGENCY,
    color: '#b91c1c', // Rojo emergencia
    contrastColor: '#ffffff',
    label: 'Emergencias',
    iconPath: ICON_PATHS.emergency,
    emoji: '🚨',
    category: 'emergency',
  },

  [InfrastructureType.MAREMOTO]: {
    type: InfrastructureType.MAREMOTO,
    color: '#0891b2', // Cian-600 (ola/agua)
    contrastColor: '#ffffff',
    label: 'Maremoto',
    iconPath: ICON_PATHS.emergency, // reutiliza icono alerta hasta tener path dedicado
    emoji: '🌊',
    category: 'emergency',
  },

  // === SERVICIOS (verdes/azules) ===
  [InfrastructureType.EDUCATION]: {
    type: InfrastructureType.EDUCATION,
    color: '#059669', // Verde educación
    contrastColor: '#ffffff',
    label: 'Educativo',
    iconPath: ICON_PATHS.education,
    emoji: '🏫',
    category: 'services',
  },
  [InfrastructureType.CULTURAL]: {
    type: InfrastructureType.CULTURAL,
    color: '#7c3aed', // Violeta cultura
    contrastColor: '#ffffff',
    label: 'Cultural',
    iconPath: ICON_PATHS.cultural,
    emoji: '🏛️',
    category: 'services',
  },
  [InfrastructureType.RELIGIOUS]: {
    type: InfrastructureType.RELIGIOUS,
    color: '#6366f1', // Índigo religioso
    contrastColor: '#ffffff',
    label: 'Religioso',
    iconPath: ICON_PATHS.religious,
    emoji: '⛪',
    category: 'services',
  },
  [InfrastructureType.SPORTS]: {
    type: InfrastructureType.SPORTS,
    color: '#0891b2', // Cyan deporte
    contrastColor: '#ffffff',
    label: 'Deportivo',
    iconPath: ICON_PATHS.sports,
    emoji: '⚽',
    category: 'services',
  },
  [InfrastructureType.SOCIAL]: {
    type: InfrastructureType.SOCIAL,
    color: '#0d9488', // Teal social
    contrastColor: '#ffffff',
    label: 'Social',
    iconPath: ICON_PATHS.social,
    emoji: '🤝',
    category: 'services',
  },

  // === UTILITIES (amarillos/naranjas) ===
  [InfrastructureType.FUEL]: {
    type: InfrastructureType.FUEL,
    color: '#ca8a04', // Amarillo combustible
    contrastColor: '#000000',
    label: 'Combustible',
    iconPath: ICON_PATHS.fuel,
    emoji: '⛽',
    category: 'utilities',
  },
  [InfrastructureType.HYDRAULIC]: {
    type: InfrastructureType.HYDRAULIC,
    color: '#0284c7', // Azul agua
    contrastColor: '#ffffff',
    label: 'Hidráulico',
    iconPath: ICON_PATHS.hydraulic,
    emoji: '💧',
    category: 'utilities',
  },
  [InfrastructureType.ENERGY]: {
    type: InfrastructureType.ENERGY,
    color: '#eab308', // Amarillo energía
    contrastColor: '#000000',
    label: 'Energía',
    iconPath: ICON_PATHS.energy,
    emoji: '⚡',
    category: 'utilities',
  },
  [InfrastructureType.TELECOM]: {
    type: InfrastructureType.TELECOM,
    color: '#64748b', // Gris telecom
    contrastColor: '#ffffff',
    label: 'Telecom',
    iconPath: ICON_PATHS.telecom,
    emoji: '📡',
    category: 'utilities',
  },

  // === INFRAESTRUCTURA (grises/marrones) ===
  [InfrastructureType.MUNICIPAL]: {
    type: InfrastructureType.MUNICIPAL,
    color: '#475569', // Gris municipal
    contrastColor: '#ffffff',
    label: 'Municipal',
    iconPath: ICON_PATHS.municipal,
    emoji: '🏛️',
    category: 'infrastructure',
  },
  [InfrastructureType.VIAL]: {
    type: InfrastructureType.VIAL,
    color: '#78716c', // Gris piedra vial
    contrastColor: '#ffffff',
    label: 'Vial',
    iconPath: ICON_PATHS.vial,
    emoji: '🛣️',
    category: 'infrastructure',
  },
  [InfrastructureType.INDUSTRIAL]: {
    type: InfrastructureType.INDUSTRIAL,
    color: '#57534e', // Gris industrial
    contrastColor: '#ffffff',
    label: 'Industrial',
    iconPath: ICON_PATHS.industrial,
    emoji: '🏭',
    category: 'infrastructure',
  },
  [InfrastructureType.RSU]: {
    type: InfrastructureType.RSU,
    color: '#84cc16', // Lima RSU
    contrastColor: '#000000',
    label: 'RSU',
    iconPath: ICON_PATHS.industrial, // Reutiliza icono industrial por ahora
    emoji: '♻️',
    category: 'infrastructure',
  },
  [InfrastructureType.PORT]: {
    type: InfrastructureType.PORT,
    color: '#0891b2', // Cyan puerto
    contrastColor: '#ffffff',
    label: 'Puerto',
    iconPath: ICON_PATHS.generic, // TODO: Añadir icono puerto específico
    emoji: '⚓',
    category: 'infrastructure',
  },
  [InfrastructureType.GENERIC]: {
    type: InfrastructureType.GENERIC,
    color: '#94a3b8', // Gris neutro
    contrastColor: '#000000',
    label: 'Genérico',
    iconPath: ICON_PATHS.generic,
    emoji: '📍',
    category: 'infrastructure',
  },
};

// ============================================================================
// FUNCIONES DE GENERACIÓN
// ============================================================================

/**
 * Obtiene configuración de marcador por tipo
 */
export function getMarkerConfig(type: InfrastructureType): MarkerConfig {
  return MARKER_CONFIGS[type] || MARKER_CONFIGS[InfrastructureType.GENERIC];
}

/**
 * Genera SVG de marcador como string (para Leaflet divIcon o img src)
 */
export function generateMarkerSvg(
  type: InfrastructureType,
  options: MarkerSvgOptions = {}
): string {
  const { size = 'medium', selected = false, disabled = false, withShadow = true } = options;
  const config = getMarkerConfig(type);
  const dims = MARKER_SIZES[size];
  
  // Colores según estado
  const fillColor = disabled ? '#9ca3af' : selected ? '#1e40af' : config.color;
  const strokeColor = selected ? '#fbbf24' : disabled ? '#6b7280' : darkenColor(config.color, 20);
  const iconColor = disabled ? '#4b5563' : config.contrastColor;
  
  // Escalar icono
  const iconSize = 14 * dims.iconScale;
  const iconX = (dims.width - iconSize) / 2;
  const iconY = dims.height * 0.2;
  
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${dims.width}" height="${dims.height}" viewBox="0 0 ${dims.width} ${dims.height}">
  ${withShadow ? `<ellipse cx="${dims.width / 2}" cy="${dims.height - 2}" rx="${dims.width * 0.3}" ry="2" fill="rgba(0,0,0,0.2)"/>` : ''}
  <path d="M${dims.width / 2} ${dims.height - 4}
           L${dims.width * 0.15} ${dims.height * 0.45}
           A${dims.width * 0.42} ${dims.width * 0.42} 0 1 1 ${dims.width * 0.85} ${dims.height * 0.45}
           Z" 
        fill="${fillColor}" 
        stroke="${strokeColor}" 
        stroke-width="1.5"/>
  <g transform="translate(${iconX}, ${iconY}) scale(${dims.iconScale})">
    <path d="${config.iconPath}" 
          fill="none" 
          stroke="${iconColor}" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round"/>
  </g>
</svg>`;
}

/**
 * Genera URL data: para usar como src de imagen
 */
export function getMarkerDataUrl(
  type: InfrastructureType,
  options: MarkerSvgOptions = {}
): string {
  const svg = generateMarkerSvg(type, options);
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Obtiene anchor point para el marcador (punta inferior)
 */
export function getMarkerAnchor(size: MarkerSize = 'medium'): [number, number] {
  const dims = MARKER_SIZES[size];
  return [dims.width / 2, dims.height];
}

/**
 * Obtiene popup anchor (sobre el marcador)
 */
export function getPopupAnchor(size: MarkerSize = 'medium'): [number, number] {
  const dims = MARKER_SIZES[size];
  return [0, -dims.height + 5];
}

/**
 * Agrupa tipos por categoría
 */
export function getTypesByCategory(): Record<string, InfrastructureType[]> {
  const groups: Record<string, InfrastructureType[]> = {
    emergency: [],
    services: [],
    utilities: [],
    infrastructure: [],
  };
  
  for (const [type, config] of Object.entries(MARKER_CONFIGS)) {
    groups[config.category].push(type as InfrastructureType);
  }
  
  return groups;
}

/**
 * Obtiene todos los colores únicos (para leyenda)
 */
export function getAllMarkerColors(): Array<{ type: InfrastructureType; color: string; label: string }> {
  return Object.values(MARKER_CONFIGS).map(c => ({
    type: c.type,
    color: c.color,
    label: c.label,
  }));
}

/**
 * Verifica si un color cumple contraste mínimo
 */
export function meetsContrastRatio(
  foreground: string,
  background: string,
  minRatio: number = 3
): boolean {
  const getLuminance = (hex: string): number => {
    const rgb = hexToRgb(hex);
    const [r, g, b] = rgb.map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  
  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  
  return ratio >= minRatio;
}

// ============================================================================
// UTILIDADES INTERNAS
// ============================================================================

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  const darkened = rgb.map(c => Math.max(0, Math.floor(c * (1 - percent / 100))));
  return `#${darkened.map(c => c.toString(16).padStart(2, '0')).join('')}`;
}
